import { CalendarDays, DollarSign, ShoppingCart, TrendingUp } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { RevenueOverviewResult } from '../../../../api/widgetTelemetry';
import StatCard from '../shared/StatCard';

interface RevenueOverviewProps {
  data: RevenueOverviewResult | null;
  loading: boolean;
}

const GA4_PURCHASES_REPORT_URL =
  'https://analytics.google.com/analytics/web/#/p530668124/reports/explorer?params=_u..nav%3Dmaui&collectionId=business-objectives&r=ecomm-purchases';

function usd(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function usdShort(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1000)}k`;
  return `$${Math.round(n)}`;
}

function pct(n: number): string {
  return `${(n * 100).toFixed(2)}%`;
}

export default function RevenueOverview({ data, loading }: RevenueOverviewProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="bg-white dark:bg-[#1a1f1c] rounded-lg p-4 border border-stone-200 dark:border-stone-700 animate-pulse h-24"
          />
        ))}
      </div>
    );
  }

  if (!data) return null;

  const { summary, byVenue, byRegion, overTime, notes } = data;

  return (
    <div className="space-y-6">
      <div className="bg-moss-50 dark:bg-moss-900/20 border border-moss-200 dark:border-moss-800 rounded-lg p-3 flex items-start justify-between gap-4">
        <p className="text-xs text-moss-700 dark:text-moss-400">
          <strong>Sources.</strong> Bookings + revenue come from{' '}
          <code>gold.fact_booking</code> (FareHarbor webhook → warehouse). Widget
          sessions are GA4 <code>widget_loaded</code> events on property{' '}
          <code>530668124</code> for the same window. Per-session attribution
          (which user fired which <code>purchase</code>) lives in GA4 directly
          via FareHarbor&apos;s native cross-domain GA4 integration — open the
          report link for the FH-fired purchases →
        </p>
        <a
          href={GA4_PURCHASES_REPORT_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-moss-700 dark:text-moss-400 underline whitespace-nowrap shrink-0 mt-0.5"
        >
          Open GA4 Ecommerce Purchases →
        </a>
      </div>

      {notes.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 space-y-1">
          {notes.map((n, i) => (
            <p key={i} className="text-xs text-amber-700 dark:text-amber-400">
              {n}
            </p>
          ))}
        </div>
      )}

      <div className="grid grid-cols-4 gap-4">
        <StatCard
          label="Total Revenue"
          value={usd(summary.totalRevenue)}
          icon={DollarSign}
          iconColor="text-moss-600"
        />
        <StatCard
          label="Bookings"
          value={summary.totalBookings.toLocaleString()}
          icon={ShoppingCart}
          iconColor="text-amber-600"
        />
        <StatCard
          label="Avg Booking Value"
          value={usd(summary.averageBookingValue)}
          icon={CalendarDays}
          iconColor="text-water-600"
        />
        <StatCard
          label="Conversion Rate"
          value={
            summary.conversionRate === null
              ? '—'
              : `${pct(summary.conversionRate)} (${summary.totalBookings.toLocaleString()} / ${(summary.widgetSessions ?? 0).toLocaleString()})`
          }
          icon={TrendingUp}
          iconColor="text-moss-600"
        />
      </div>

      <div className="bg-white dark:bg-[#1a1f1c] rounded-lg border border-stone-200 dark:border-stone-700 p-4">
        <h3 className="text-sm font-medium text-bark-900 dark:text-stone-100 mb-3">
          Daily revenue trend
        </h3>
        {overTime.length === 0 ? (
          <p className="text-xs text-stone-400 text-center py-12">
            No bookings in this range yet.
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={overTime} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid
                strokeDasharray="3 3"
                className="stroke-stone-200 dark:stroke-stone-700"
              />
              <XAxis
                dataKey="date"
                tickFormatter={(v: string) => v.slice(5)}
                tick={{ fontSize: 11 }}
              />
              <YAxis
                tickFormatter={(v: number) => usdShort(v)}
                tick={{ fontSize: 11 }}
                width={56}
              />
              <Tooltip
                formatter={(value, name) => [
                  name === 'revenue' && typeof value === 'number'
                    ? usd(value)
                    : Number(value).toLocaleString(),
                  String(name),
                ]}
                contentStyle={{ fontSize: 12 }}
              />
              <Line
                type="monotone"
                dataKey="revenue"
                stroke="#5e8b4a"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="bg-white dark:bg-[#1a1f1c] rounded-lg border border-stone-200 dark:border-stone-700 p-4">
          <h3 className="text-sm font-medium text-bark-900 dark:text-stone-100 mb-3">
            Revenue by region
          </h3>
          {byRegion.length === 0 ? (
            <p className="text-xs text-stone-400 text-center py-8">No region data.</p>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart
                  data={byRegion.slice(0, 10)}
                  margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                  layout="vertical"
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    className="stroke-stone-200 dark:stroke-stone-700"
                    horizontal={false}
                  />
                  <XAxis
                    type="number"
                    tickFormatter={(v: number) => usdShort(v)}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                  <Tooltip
                    formatter={(value) => (typeof value === 'number' ? usd(value) : '—')}
                    contentStyle={{ fontSize: 12 }}
                  />
                  <Bar dataKey="revenue" fill="#5e8b4a" />
                </BarChart>
              </ResponsiveContainer>
              <table className="w-full text-xs mt-2">
                <thead className="text-stone-500 dark:text-stone-400">
                  <tr>
                    <th className="text-left py-1">Region</th>
                    <th className="text-right py-1">Bookings</th>
                    <th className="text-right py-1">Revenue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100 dark:divide-stone-800">
                  {byRegion.map((r) => (
                    <tr key={r.key}>
                      <td className="py-1 text-bark-900 dark:text-stone-200">{r.name}</td>
                      <td className="py-1 text-right text-stone-600 dark:text-stone-400">
                        {r.bookings.toLocaleString()}
                      </td>
                      <td className="py-1 text-right text-bark-900 dark:text-stone-200">
                        {usd(r.revenue)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>

        <div className="bg-white dark:bg-[#1a1f1c] rounded-lg border border-stone-200 dark:border-stone-700 p-4 overflow-hidden">
          <h3 className="text-sm font-medium text-bark-900 dark:text-stone-100 mb-3">
            Top venues by revenue (top 25)
          </h3>
          {byVenue.length === 0 ? (
            <p className="text-xs text-stone-400 text-center py-8">No venue data.</p>
          ) : (
            <div className="overflow-y-auto max-h-[440px]">
              <table className="w-full text-xs">
                <thead className="text-stone-500 dark:text-stone-400 sticky top-0 bg-white dark:bg-[#1a1f1c]">
                  <tr>
                    <th className="text-left py-1">Venue</th>
                    <th className="text-right py-1">Bookings</th>
                    <th className="text-right py-1">Revenue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100 dark:divide-stone-800">
                  {byVenue.map((v) => (
                    <tr key={v.key}>
                      <td className="py-1 text-bark-900 dark:text-stone-200">{v.name}</td>
                      <td className="py-1 text-right text-stone-600 dark:text-stone-400">
                        {v.bookings.toLocaleString()}
                      </td>
                      <td className="py-1 text-right text-bark-900 dark:text-stone-200">
                        {usd(v.revenue)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
